import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNotifications, markNotificationAsRead } from '../../../services/notificationService'; // adjust path as needed

interface Notification {
  id: string;
  title: string;
  message: string;
  date: string;
  time: string;
  isRead: boolean;
  url?: string;
}

const formatDateTime = (isoString: string) => {
  const dt = new Date(isoString);

  const date = dt.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const time = dt.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  return { date, time };
};

const NotificationList = () => {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [initialLoading, setInitialLoading] = useState(true); // only for first load
  const [pageLoading, setPageLoading] = useState(false); // for page switches
  const [error, setError] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const loadNotifications = async (page: number, isInitial = false) => {
    if (isInitial) {
      setInitialLoading(true);
    } else {
      setPageLoading(true);
    }
    setError('');

    try {
      const response = await getNotifications(page);

      const list = response.notifications?.data ?? [];

      const mapped: Notification[] = list.map((item: any) => {
        const { date, time } = formatDateTime(item.created_at);

        return {
          id: item.id,
          title: item.title,
          message: item.message,
          date,
          time,
          isRead: !!item.read_at,
          url: item.url,
        };
      });

      setNotifications(mapped);
      setCurrentPage(response.notifications?.current_page ?? page);
      setLastPage(response.notifications?.last_page ?? 1);
      setTotal(response.notifications?.total ?? 0);
    } catch (err) {
      console.error(err);
      setError('Failed to load notifications.');
    } finally {
      setInitialLoading(false);
      setPageLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications(1, true);
  }, []);

  const handleClick = async (notification: Notification) => {
    // Optimistic local update — turns the row red immediately
    if (!notification.isRead) {
      setNotifications((prev) => prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n)));

      // Persist to backend
      try {
        await markNotificationAsRead(notification.id);
      } catch (err) {
        console.error(err);
        // Revert if the API call fails
        setNotifications((prev) => prev.map((n) => (n.id === notification.id ? { ...n, isRead: false } : n)));
      }
    }

    if (notification.url) {
      try {
        const path = new URL(notification.url).pathname;
        setTimeout(() => navigate(path), 150); // let the bg transition render first
      } catch {
        setTimeout(() => window.open(notification.url, '_blank'), 150);
      }
    }
  };

  if (initialLoading) {
    return <div className="p-4 text-sm text-gray-500">Loading notifications...</div>;
  }

  if (error) {
    return <div className="p-4 text-sm text-red-500">{error}</div>;
  }

  return (
    <div className="w-full mx-auto bg-white">
      <div className={`divide-y divide-gray-200 transition-opacity duration-150 ${pageLoading ? 'opacity-50' : 'opacity-100'}`}>
        {notifications.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">No notifications found.</div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => handleClick(notification)}
              className={`p-4 transition-colors duration-150 ${
                !notification.isRead ? 'bg-blue-50' : 'bg-white'
              } ${notification.url ? 'cursor-pointer hover:bg-gray-50' : ''}`}
            >
              <h3 className={`text-base font-medium mb-1 ${!notification.isRead ? 'text-gray-900' : 'text-gray-600'}`}>
                {notification.title}
              </h3>
              <p className="text-gray-600 text-sm mb-2">{notification.message}</p>
              <p className="text-gray-500 text-sm">
                {notification.date} {notification.time}
              </p>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {lastPage > 1 && (
        <div className="flex justify-between items-center px-4 py-3 border-t">
          <p className="text-sm text-gray-500">
            Page {currentPage} of {lastPage}
          </p>
          <div className="flex gap-2">
            <button
              disabled={currentPage <= 1 || pageLoading}
              onClick={() => loadNotifications(currentPage - 1)}
              className="px-3 py-1 text-sm border rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              disabled={currentPage >= lastPage || pageLoading}
              onClick={() => loadNotifications(currentPage + 1)}
              className="px-3 py-1 text-sm border rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationList;
